import { Check, Minus } from "@phosphor-icons/react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from "react";

import { cn } from "common/helpers/utils";

const switchVariants = cva(
  [
    "relative inline-flex shrink-0 cursor-pointer overflow-hidden rounded-full transition-colors",
    "data-[state=unchecked]:bg-black-300",
    "disabled:cursor-not-allowed disabled:bg-black-300 disabled:text-black-300",
    "focus-visible:outline-none",
  ],
  {
    variants: {
      size: {
        sm: "h-4 w-7 p-0.5",
        md: "h-[22px] w-10 p-[3px]",
      },
      color: {
        green: "text-green-400 data-[state=checked]:bg-green-400",
        purple: "text-purple-400 data-[state=checked]:bg-purple-400",
        black: "text-black-950 data-[state=checked]:bg-black-950",
      },
    },
    defaultVariants: {
      size: "md",
      color: "green",
    },
  },
);

type SwitchProps = ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> &
  VariantProps<typeof switchVariants>;

const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = "md", color, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchVariants({ size, color, className }))}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn([
        "peer pointer-events-none rounded-full my-auto bg-white shadow-[0px_3px_4px_-2px_rgba(0,0,0,0.24)] data-[disabled]:shadow-none transition-transform h-5 data-[disabled]:bg-black-100",
        size === "md"
          ? "size-4 data-[state=checked]:translate-x-[110%]"
          : "size-3 data-[state=checked]:translate-x-[100%]",
      ])}
    />
    <Check
      size={size === "md" ? 12 : 8}
      weight="bold"
      className={cn([
        "absolute opacity-0 transition-all",
        size === "md"
          ? "right-[23px] top-[5px] peer-data-[state=checked]:right-[5px] peer-data-[state=checked]:opacity-100"
          : "right-[23px] top-1 peer-data-[state=checked]:right-1 opacity-0 peer-data-[disabled]:peer-data-[state=checked]:opacity-100",
      ])}
    />
    <Minus
      weight="bold"
      className={cn([
        "absolute transition-all peer-data-[disabled]:opacity-100 opacity-0",
        size === "md" ? "right-6 top-[6px]" : "right-4 top-1",
      ])}
      size={size === "md" ? 11 : 8}
    />
  </SwitchPrimitives.Root>
));

Switch.displayName = SwitchPrimitives.Root.displayName;

export default Switch;
